import React, { useState } from 'react';

/**
 * Componente inline para importar contactos a una campaña
 * Se muestra como fila expandible dentro de la tabla de campañas
 */
const InlineCampaignImporter = ({ campaign, onClose, onSuccess, setImportingFile }) => {
  const [dragActive, setDragActive] = useState(false);
  const [importStatus, setImportStatus] = useState('idle'); // 'idle', 'uploading', 'success', 'error'
  const [importResult, setImportResult] = useState(null);

  const handleFileUpload = async (file) => {
    if (!file) {
      console.error('❌ No se recibió archivo');
      return;
    }

    console.log('🚀 Iniciando importación para campaña:', campaign.id);
    console.log('📎 Archivo recibido:', {
      name: file.name,
      size: file.size,
      type: file.type
    });
    
    setImportStatus('uploading');
    setImportingFile(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('excel_file', file);
    formData.append('campaign_id', campaign.id);

    try {
      console.log('📤 Enviando archivo:', file.name, 'Tamaño:', file.size);
      
      const response = await fetch('/sistemasms/backend/api/campaigns/import-contacts.php', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      console.log('📡 Status de respuesta:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      console.log('📄 Respuesta cruda del servidor:', responseText);

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Error parseando JSON:', parseError);
        throw new Error('Respuesta del servidor no es JSON válido: ' + responseText.substring(0, 100));
      }

      console.log('📊 Resultado parseado completo:', result);
      
      if (result.success) {
        let contactsImported = 0;
        let totalRows = 0;
        let failedImports = 0;

        if (result.data) {
          contactsImported = result.data.successful_imports || result.data.total_imported || result.data.imported_count || 0;
          totalRows = result.data.total_rows || result.data.processed_rows || 0;
          failedImports = result.data.failed_imports || result.data.errors_count || 0;
        } else {
          contactsImported = result.successful_imports || result.total_imported || result.imported_count || 0;
          totalRows = result.total_rows || result.processed_rows || 0;
          failedImports = result.failed_imports || result.errors_count || 0;
        }

        if (contactsImported === 0 && result.message) {
          const match = result.message.match(/(\d+)\s*contactos?\s*importados?/i);
          if (match) {
            contactsImported = parseInt(match[1]);
          }
        }

        console.log('✅ Importación exitosa - Datos extraídos:', {
          contactsImported,
          totalRows,
          failedImports,
          rawResult: result
        });

        setImportResult({
          success: true,
          contactsImported,
          totalRows,
          failedImports,
          errors: result.errors || result.data?.errors || []
        });
        setImportStatus('success');

        let mensaje = `✅ Importación completada para "${campaign.campaign_name}"!\n\n`;
        mensaje += `📊 RESULTADOS:\n`;
        mensaje += `• Contactos importados: ${contactsImported}\n`;
        if (totalRows > 0) {
          mensaje += `• Total de filas procesadas: ${totalRows}\n`;
        }
        if (failedImports > 0) {
          mensaje += `• Filas con errores: ${failedImports}\n`;
        }
        
        const errors = result.errors || result.data?.errors || [];
        if (errors.length > 0) {
          mensaje += `\n⚠️ ERRORES ENCONTRADOS:\n`;
          errors.slice(0, 3).forEach(error => {
            mensaje += `• ${error}\n`;
          });
          if (errors.length > 3) {
            mensaje += `• ... y ${errors.length - 3} errores más\n`;
          }
        }

        if (contactsImported === 0) {
          mensaje += `\n🐛 DEBUG - Respuesta del servidor:\n${JSON.stringify(result, null, 2).substring(0, 200)}...`;
        }

        alert(mensaje);

        if (onSuccess) {
          onSuccess({
            total_imported: contactsImported,
            successful_imports: contactsImported,
            failed_imports: failedImports
          });
        }

        setTimeout(() => {
          onClose();
        }, 2000);

      } else {
        throw new Error(result.message || 'Error desconocido en la importación');
      }
    } catch (error) {
      console.error('❌ Error en importación:', error);
      setImportStatus('error');
      setImportResult({
        success: false,
        error: error.message
      });
      
      alert('❌ Error importando archivo:\n\n' + error.message + 
            '\n\nRevisa la consola del navegador para más detalles.');
    } finally {
      setImportingFile(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.type.includes('sheet') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      handleFileUpload(file);
    } else {
      alert('Por favor, selecciona un archivo Excel (.xlsx o .xls)');
    }
  };

  const handleFileSelect = (e) => {
    console.log('📎 Archivo seleccionado:', e.target.files);
    const file = e.target.files[0];
    if (file) {
      console.log('✅ Procesando archivo:', file.name, 'Tamaño:', file.size);
      handleFileUpload(file);
      e.target.value = '';
    } else {
      console.warn('⚠️ No se seleccionó ningún archivo');
    }
  };

  return (
    <tr>
      <td colSpan="5" className="p-0">
        <div className="bg-blue-50 border-l-4 border-blue-400 mx-4 my-2 rounded-r-lg">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                👥 Importar Contactos - {campaign.campaign_name}
              </h4>
              <button 
                onClick={onClose}
                className="text-blue-600 hover:text-blue-800 text-xl"
              >
                ✕
              </button>
            </div>

            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 ${
                dragActive 
                  ? 'border-blue-500 bg-blue-100 scale-105' 
                  : importStatus === 'success'
                  ? 'border-green-500 bg-green-50'
                  : importStatus === 'error'
                  ? 'border-red-500 bg-red-50'
                  : 'border-blue-300 bg-white'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
            >
              {importStatus === 'uploading' ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-600 border-t-transparent"></div>
                  <span className="text-blue-800 font-medium">Procesando archivo...</span>
                  <div className="text-sm text-blue-600">
                    Por favor espera mientras procesamos tu archivo Excel
                  </div>
                </div>
              ) : importStatus === 'success' && importResult ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-2xl">✓</span>
                  </div>
                  <span className="text-green-800 font-bold text-lg">
                    ¡{importResult.contactsImported} contactos importados!
                  </span>
                  <div className="text-sm text-green-700">
                    {importResult.failedImports > 0 && 
                      `${importResult.failedImports} filas con errores`
                    }
                  </div>
                </div>
              ) : importStatus === 'error' && importResult ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-2xl">✕</span>
                  </div>
                  <span className="text-red-800 font-bold">Error en importación</span>
                  <div className="text-sm text-red-700 max-w-md">
                    {importResult.error}
                  </div>
                  <button 
                    onClick={() => setImportStatus('idle')}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                  >
                    Intentar de nuevo
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                      <span className="text-3xl">📊</span>
                    </div>
                  </div>
                  <p className="text-blue-800 font-medium mb-2 text-lg">
                    Arrastra tu archivo Excel aquí
                  </p>
                  <p className="text-blue-600 text-sm mb-4">
                    o haz clic para seleccionar • Formatos: .xlsx, .xls • Máximo: 10MB
                  </p>
                  <label className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 cursor-pointer inline-block transition-colors text-sm font-medium shadow-sm">
                    📁 Seleccionar Archivo Excel
                    <input 
                      type="file" 
                      accept=".xlsx,.xls" 
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                </>
              )}
            </div>

            <div className="mt-4 p-3 bg-blue-100 rounded-lg text-sm">
              <p className="font-semibold text-blue-900 mb-2">Formato requerido:</p>
              <p className="text-blue-800 mb-1"><strong>Columnas obligatorias:</strong> nombre, telefono</p>
              <p className="text-blue-800"><strong>Columnas opcionales:</strong> email, empresa, rut</p>
              <p className="text-blue-600 text-xs mt-2">
                Los contactos se agregarán específicamente a esta campaña
              </p>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

export default InlineCampaignImporter;

